import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { message } from "antd";

const SupportWebSocketContext = createContext(null);

export const useSupportWebSocket = () => {
    const context = useContext(SupportWebSocketContext);
    if (!context) {
        throw new Error("useSupportWebSocket must be used within SupportWebSocketProvider");
    }
    return context;
};

export const SupportWebSocketProvider = ({ children }) => {
    const socketRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const [isConnected, setIsConnected] = useState(false);

    // Запрос разрешения на браузерные уведомления
    useEffect(() => {
        if ("Notification" in window && Notification.permission === "default") {
            Notification.requestPermission();
        }
    }, []);

    // Функция для показа уведомления
    const showNotification = (title, body, ticketId = null) => {
        // Показываем toast уведомление через antd
        message.info({
            content: (
                <div>
                    <div className="font-semibold">{title}</div>
                    <div className="text-sm text-gray-400">{body}</div>
                </div>
            ),
            duration: 5,
            onClick: () => {
                if (ticketId && window.location.pathname !== "/chat") {
                    window.location.href = `/chat`;
                }
            },
        });

        // Показываем браузерное уведомление, если разрешено
        if ("Notification" in window && Notification.permission === "granted") {
            const notification = new Notification(title, {
                body: body,
                icon: "/logo192.png",
                tag: `ticket-${ticketId}`,
                requireInteraction: false,
            });

            notification.onclick = () => {
                window.focus();
                if (ticketId && window.location.pathname !== "/chat") {
                    window.location.href = `/chat`;
                }
                notification.close();
            };

            // Автоматически закрываем через 5 секунд
            setTimeout(() => {
                notification.close();
            }, 5000);
        }
    };

    useEffect(() => {
        // Проверяем, доступен ли socket.io-client
        let io;
        try {
            io = require("socket.io-client");
        } catch (e) {
            console.warn("socket.io-client не установлен. WebSocket функции будут недоступны.");
            return;
        }

        const connect = () => {
            if (socketRef.current?.connected) return;

            // Подключение согласно рекомендациям бэкенда
            const socket = io("https://api.duotetra.quest/support", {
                withCredentials: true,
                transports: ["websocket"],
                path: "/socket.io",
                reconnection: true,
                reconnectionDelay: 1000,
                reconnectionDelayMax: 5000,
                reconnectionAttempts: Infinity,
            });

            socket.on("connect", () => {
                console.log("✅ Глобальное WebSocket подключение установлено");
                setIsConnected(true);
                if (reconnectTimeoutRef.current) {
                    clearTimeout(reconnectTimeoutRef.current);
                    reconnectTimeoutRef.current = null;
                }
            });

            socket.on("connect_error", (error) => {
                console.error("❌ Ошибка подключения WebSocket:", error);
                setIsConnected(false);
            });

            socket.on("connected", (data) => {
                console.log("✅ Подтверждение подключения:", data);
            });

            socket.on("ticket:new", (data) => {
                console.log("📨 Новый тикет:", data.ticket);
                const ticket = data.ticket;
                showNotification(
                    "Новый тикет",
                    `Тикет #${ticket.id} от ${ticket.telegramUsername || `ID: ${ticket.userId}`}`,
                    ticket.id
                );
            });

            socket.on("ticket:message", (data) => {
                console.log("💬 Новое сообщение:", data);
                const { ticketId, message: msg } = data;
                
                // Показываем уведомление только если это не наше сообщение
                // (сообщения от саппорта не должны показывать уведомления)
                if (msg.messageType === "USER") {
                    const username = msg.fromUserId ? `ID: ${msg.fromUserId}` : "Пользователь";
                    showNotification(
                        "Новое сообщение в тикете",
                        `${username}: ${msg.content?.substring(0, 50)}${msg.content?.length > 50 ? "..." : ""}`,
                        ticketId
                    );
                }
            });

            socket.on("ticket:update", (data) => {
                console.log("🔄 Тикет обновлен:", data.ticket);
                // Можно добавить уведомления для важных обновлений
            });

            socket.on("disconnect", (reason) => {
                console.log("⚠️ WebSocket отключен:", reason);
                setIsConnected(false);
                
                if (reason === "io server disconnect") {
                    socket.connect();
                } else if (reason !== "io client disconnect") {
                    reconnectTimeoutRef.current = setTimeout(() => {
                        connect();
                    }, 5000);
                }
            });

            socket.on("error", (error) => {
                console.error("❌ WebSocket error:", error);
            });

            socketRef.current = socket;
        };

        connect();

        return () => {
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
            }
            if (socketRef.current) {
                socketRef.current.disconnect();
                socketRef.current = null;
            }
            setIsConnected(false);
        };
    }, []);

    const value = {
        socket: socketRef.current,
        isConnected,
    };

    return (
        <SupportWebSocketContext.Provider value={value}>
            {children}
        </SupportWebSocketContext.Provider>
    );
};
